import de from "@/locales/de";
import en from "@/locales/en";

export type Locale = "de" | "en";

type DeepKeys<T, Prefix extends string = ""> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? DeepKeys<T[K], `${Prefix}${K}.`>
          : `${Prefix}${K}`
        : never;
    }[keyof T]
  : never;

export type TranslationKey = DeepKeys<typeof de>;

export const SUPPORTED_LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "en", label: "English", flag: "🇬🇧" },
];

export const DEFAULT_LOCALE: Locale = "de";

const translations: Record<Locale, Record<string, unknown>> = { de, en };

export function isValidLocale(value: unknown): value is Locale {
  return value === "de" || value === "en";
}

function detectLocale(): Locale | null {
  const lang = navigator.language?.slice(0, 2).toLowerCase();
  if (lang === "de") return "de";
  if (lang === "en") return "en";
  return null;
}

export function getStoredLocale(): Locale {
  const runtime = (window as any).__AYU_DEFAULT_LANG__;
  if (isValidLocale(runtime)) return runtime;
  const browser = detectLocale();
  if (browser) return browser;
  return DEFAULT_LOCALE;
}

function getNestedValue(obj: Record<string, unknown>, key: string): string | undefined {
  const parts = key.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === "string" ? current : undefined;
}

export function translate(
  locale: Locale,
  key: TranslationKey,
  params?: Record<string, string>
): string {
  const dict = translations[locale] as Record<string, unknown>;
  const fallback = translations[DEFAULT_LOCALE] as Record<string, unknown>;
  let value = getNestedValue(dict, key) ?? getNestedValue(fallback, key) ?? key;

  if (params) {
    for (const [k, v] of Object.entries(params)) {
      value = value.replace(new RegExp(`\\{${k}\\}`, "g"), v);
    }
  }

  return value;
}
